import _ from 'lodash';

// A member counts as done once their own status reaches PARTIAL_COMPLETE: the
// group status only turns COMPLETED when every member is done, so ranking on
// COMPLETED alone would give every member the same score.
const doneStatuses = ['PARTIAL_COMPLETE', 'COMPLETED'];

const isDone = status => _.includes(doneStatuses, status);

const partnersOf = sessionStatus =>
  _.get(sessionStatus, 'partnerCompletedSteps') || [];

// `mysessions` returns one row per session the group has begun, carrying the
// viewer's own status plus a status for every other member. Tallying the done
// ones across all rows gives each member's completed-session count.
// `members` seeds the tally with the group roster, so members who have no
// status row yet still appear on a count of zero.
export const sessionLeaderboard = (ongoingSessions, self = {}, members = []) => {
  const rows = _.filter(ongoingSessions, session => _.get(session, 'id'));
  const selfId = _.get(self, 'id');
  const selfName = `${_.get(self, 'firstName', '')} ${_.get(
    self,
    'lastName',
    '',
  )}`.trim();

  const entries = new Map();

  const tally = (userId, name, done) => {
    const key = String(userId);
    const entry = entries.get(key) || {
      userId,
      name,
      isSelf: userId === selfId,
      completedCount: 0,
    };
    // Later rows carry the same name; keep the first non-empty one.
    if (!entry.name && name) {
      entry.name = name;
    }
    if (done) {
      entry.completedCount += 1;
    }
    entries.set(key, entry);
  };

  // Seed every known group member at zero so the card still lists the whole
  // group before any session has been started. Rows below only add to these.
  members.forEach(member => {
    const memberId = _.get(member, 'userId');
    if (!_.isNil(memberId)) {
      tally(memberId, _.get(member, 'name', ''), false);
    }
  });
  if (!_.isNil(selfId)) {
    tally(selfId, selfName, false);
  }

  rows.forEach(session => {
    // The viewer is absent from `partnerCompletedSteps`, so their own row comes
    // from the auth profile. Skip it entirely while the profile is unhydrated
    // rather than tallying an id-less entry.
    if (!_.isNil(selfId)) {
      tally(selfId, selfName, isDone(_.get(session, 'status')));
    }
    partnersOf(session).forEach(partner => {
      tally(
        _.get(partner, 'userId'),
        _.get(partner, 'name', ''),
        isDone(_.get(partner, 'partnerStatus')),
      );
    });
  });

  // Highest first; ties fall back to name so the order stays stable between
  // renders rather than following whatever order the API returned.
  return _.orderBy(
    [...entries.values()],
    ['completedCount', entry => _.toLower(entry.name)],
    ['desc', 'asc'],
  );
};

export const leaderboardMax = leaderboard =>
  _.get(_.maxBy(leaderboard, 'completedCount'), 'completedCount', 0);
